import { useReactiveVar } from '@apollo/client';
import { AccountMenu } from '@hulu/react-style-components';
import React, { useRef, useState } from 'react';

import useFetchUserByOrganization from '../../../apis/graphql/user/useFetchUserByOrganization';
import { selectedOrganizationIdVar } from '../../../constants';
import { isDemoEnvironment } from '../../../constants/global';
import { useOnClickOutside } from '../../../hooks';
import useSetUserFromCurrentSession from '../../../hooks/useSetUserFromCurrentSession';
import CustomDropDown from '../../CustomDropDown/CustomDropDown';
import Loader, { LoaderType } from '../../Loader';
import { DropDownItem } from '../../types';
import { USER_CARD_ITEM_TITLES, USER_CARD_ITEMS } from './constants';
import { StyledLoaderWrapper, StyledUserName } from './styled';
import { useGetUserCardItems } from './useGetUserCardItems';
import { getUserName } from './utils';

export type UserCardProps = {
  onItemClick?: (item: DropDownItem) => void;
  isBaymax?: boolean;
};

const UserCard = ({ onItemClick, isBaymax }: UserCardProps): JSX.Element | null => {
  const orgId = useReactiveVar(selectedOrganizationIdVar) || '';
  const [isUserCardOpen, setIsUserCardOpen] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  const { userInfo } = useSetUserFromCurrentSession();
  const { data: orgUser, loading } = useFetchUserByOrganization(orgId);
  const userCardItems = useGetUserCardItems({
    userCardItems: USER_CARD_ITEMS,
    userCardTitles: USER_CARD_ITEM_TITLES,
  });
  useOnClickOutside(ref, () => {
    setIsUserCardOpen(false);
    setIsActive(false);
  });

  const handleItemClick = (item: DropDownItem): void => {
    setIsUserCardOpen(false);
    setIsActive(false);
    onItemClick?.(item);
  };

  const handleClick = (): void => {
    setIsUserCardOpen(!isUserCardOpen);
    setIsActive(!isActive);
  };

  const firstName = isDemoEnvironment ? 'John' : getUserName(userInfo?.given_name, orgUser?.firstName || '');
  const lastName = isDemoEnvironment ? 'Doe' : getUserName(userInfo?.family_name, orgUser?.lastName || '');

  if (loading) {
    return (
      <StyledLoaderWrapper>
        <Loader type={LoaderType.transparent} />
      </StyledLoaderWrapper>
    );
  }

  return (
    <StyledUserName
      ref={ref}
      $isActive={isActive}
      data-testid="userCardMenu"
      $isBaymax={isBaymax}
      data-text={`${firstName} ${lastName}`}
    >
      <AccountMenu
        firstName={firstName || 'Name'}
        lastName={lastName || 'Surname'}
        onClick={handleClick}
        isOpen={isUserCardOpen}
      />
      {isUserCardOpen && <CustomDropDown handleItemClick={handleItemClick} list={userCardItems} />}
    </StyledUserName>
  );
};

export default UserCard;
